import { useEffect, useState } from 'react';
import { fetchMirrorMedia, fetchMirrorPartyMedia, getAiSession } from './ai.js';
import MirrorLens from './MirrorLens.jsx';

const objectUrls = new Map();
const inFlight = new Map();

async function resolvePrivateMedia(mediaId, partyMedia = false) {
  if (!mediaId || !getAiSession().token) return '';
  if (objectUrls.has(mediaId)) return objectUrls.get(mediaId);
  if (!inFlight.has(mediaId)) {
    inFlight.set(mediaId, (partyMedia ? fetchMirrorPartyMedia(mediaId) : fetchMirrorMedia(mediaId))
      .then(blob => {
        const url = URL.createObjectURL(blob);
        objectUrls.set(mediaId, url);
        inFlight.delete(mediaId);
        return url;
      })
      .catch(error => {
        inFlight.delete(mediaId);
        throw error;
      }));
  }
  return inFlight.get(mediaId);
}

export function clearPrivateMediaCache() {
  objectUrls.forEach(url => URL.revokeObjectURL(url));
  objectUrls.clear();
  inFlight.clear();
}

export function usePrivateImage({ source = '', mediaId = '', partyMediaId = '', fallback = '' } = {}) {
  const [resolved, setResolved] = useState(source || fallback);

  useEffect(() => {
    let active = true;
    if (source) {
      setResolved(source);
      return () => { active = false; };
    }
    const activeMediaId = partyMediaId || mediaId;
    if (!activeMediaId) {
      setResolved(fallback);
      return () => { active = false; };
    }
    setResolved(fallback);
    resolvePrivateMedia(activeMediaId, !!partyMediaId)
      .then(url => active && setResolved(url || fallback))
      .catch(() => active && setResolved(fallback));
    return () => { active = false; };
  }, [fallback, mediaId, partyMediaId, source]);

  return resolved;
}

export function PrivateImage({ source = '', mediaId = '', partyMediaId = '', fallback = '', alt = '', mirrorable = true, ...props }) {
  const src = usePrivateImage({ source, mediaId, partyMediaId, fallback });
  const image = src ? <img src={src} alt={alt} {...props} /> : <span className="private-image-placeholder" aria-label={alt || 'Private image'} />;
  return mirrorable ? <MirrorLens className="private-mirror-viewport" label={`Use ${alt || 'this image'} as a live mirror`}>{image}</MirrorLens> : image;
}
